import { useStyle } from "./style";
import { ChevronDown16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { AgentActions, AgentState, useAgentStore } from "@/common/stores/persistingStores/agentPermissions";
import { changePresence } from "@/repository/queryFns";
import { AvatarView } from "@/components/atoms/avatar/AvatarView";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { Flex } from "@/components/atoms/flex";
import { PresenceMenu } from "../../molecules/presenceMenu/PresenceMenu";
import { translateActivity } from "@/common/helpers/presenceHelper";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { useIntl } from "react-intl";
import {
	WrapUpActions,
	useWrapUpStore,
	WrapUpState,
} from "@/common/stores/nonpersistingStores/wrapup";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
	CurrentInteractionActions,
} from "@/common/stores/nonpersistingStores/currentInteraction";

import {
	interactionsQueryKey,
	supervisorActiveInteractionKey,
} from "@/repository/queryFns/interactions";
import { useQueryClient } from "@tanstack/react-query";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import Timer from "@/components/atoms/timer/Timer";

export const CurrentUserPresence = () => {
	const style = useStyle();
	const intl = useIntl();
	const agentState = useAgentStore((state: AgentState) => state);
	const setPresence = useAgentStore(
		(actions: AgentActions) => actions.setPresence
	);
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const wrapUpActions = useWrapUpStore((actions: WrapUpActions) => actions);
	const [presenceMenuOpen, setPresenceMenuOpen] = useState(false);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	const isJoinedOnAnyQueue = useQueuesStore(
		(state: QueuesState) => state.isJoinedOnAnyQueue
	);

	const queryClient = useQueryClient();
	const userId = Lockr.get(StorageKeys.UserId) as string;
	const avatarView = useMemo(
		() => (
			<AvatarView
				agentState={{
					fullName: agentState.fullName,
					availability: agentState.availability,
					activity: agentState.activity,
				}}
				photoId={userId}
				className={!isJoinedOnAnyQueue ? style.isNotJoinedBorder : ""}
			/>
		),
		[agentState, queues, userId]
	);

	const agentActivity = useMemo(
		() =>
			translateActivity(agentState.activity || agentState.availability, intl),
		[agentState]
	);

	const changePresenceHandler = (
		newAvailability: string,
		newActivity: string
	): any => {
		if (
			agentState.availability == newAvailability &&
			agentState.activity == newActivity
		) {
			return;
		}

		let presenceChangeModel = {
			sip: Lockr.get(StorageKeys.SIP),
			presence: newAvailability,
			note: newActivity,
			id: Lockr.get(StorageKeys.UserId),
			changedBy: "",
		};

		addBreadcrumb("ui_interaction", "Changing presence", presenceChangeModel);

		changePresence(presenceChangeModel)
			.then((_: any) => {
				setPresence(newAvailability, newActivity);
				togglePresenceMenuOpening();
				cancelActiveInteractionIfNeeded();
				if (wrapUp) {
					wrapUpActions.update(null);
					appStoreActions.removeCaches([CacheKey.SessionVariableText]);
				}

				appStoreActions.removeCaches([CacheKey.CallInProgress]);
			})
			.catch((err: any) => {
				captureError(err, "Error on changing presence");
				alert("Error: " + err); //TODO - notification
			});
	};

	const cancelActiveInteractionIfNeeded = () => {
		if (wrapUp && wrapUp?.mediaId === currentInteraction?.id) {
			currentInteractionActions.hideActive();
			queryClient.invalidateQueries({
				queryKey: [supervisorActiveInteractionKey],
			});
			queryClient.invalidateQueries({ queryKey: [interactionsQueryKey] });
		}
	};

	const togglePresenceMenuOpening = () => {
		setPresenceMenuOpen(!presenceMenuOpen);
	};

	const { isMobile } = useResponsiveBreakpoints();

	return (
		<Flex vAlign="flex-start">
			<div className={style.avatarContainer}>{avatarView}</div>
			<div className={style.userBasicsContainer}>
				<div className={style.userName} title={agentState.fullName}>
					{agentState.fullName}
				</div>
				{agentState.job && (
					<div className={style.userJobTitle} title={agentState.job}>
						{agentState.job}
					</div>
				)}
				<PresenceMenu
					open={presenceMenuOpen}
					trigger={
						<div
							className={style.userStatus}
							onClick={togglePresenceMenuOpening}
						>
							<div className={style.statusName} title={agentActivity}>
								{agentActivity}
							</div>
							<ChevronDown16Regular />
						</div>
					}
					positioning={
						isMobile
							? { position: "below" }
							: {
									position: "before",
									offset: { mainAxis: 80, crossAxis: 80 },
								}
					}
					queueIds={queues.map((x) => x.id)}
					onChange={changePresenceHandler}
					agentState={agentState}
					hideLock={isMobile}
					hideReasons={isMobile}
				/>
				{!!agentState.statusTimer && <Timer startDate={agentState.statusTimer} className={style.userStatus} />}
			</div>
		</Flex>
	);
};
